import {
  CustomColumnHeader,
  CustomColumnItem,
  LineItem,
  Lot,
  LineItemChanges,
  LotChanges,
  ID,
} from 'types';

const idKeys = [
  'id',
  'lot_id',
  'custom_column_header_id',
  'line_item_id',
];

const ignoredKeys = [
  ...idKeys,
  'applied_questions_state',
  'custom_column_headers',
  'deleted_qualifying_custom_column_headers',
  'line_items',
  'custom_column_items',
];

type Original = CustomColumnHeader | CustomColumnItem | LineItem | Lot;
type Changes = CustomColumnHeader | CustomColumnItem  | LineItemChanges | LotChanges;

export const updateChanges = ({
  original,
  changes,
  updates,
  preserveFields = [],
}: {
  changes: Partial<Changes>;
  original?: Original;
  preserveFields?: string[];
  updates: Partial<Changes>;
}): Partial<Changes> | undefined => {
  const newChanges: Partial<Changes> = { ...changes, ...updates };

  if (!original) return newChanges;

  // If the original attribute is equal to the update,
  // remove it from the changes (unless it's in preserveFields)
  Object.entries(updates).forEach(([k, value]) => {
    if (ignoredKeys.includes(k)) return;
    const key = k as keyof Changes;

    if (original[key] === value && !preserveFields.includes(k)) {
      delete newChanges[key];
    }
  });

  const keys = Object.keys(newChanges);
  // If there are no changes left or only ids, return undefined
  if (!keys.length || keys.every(k => idKeys.includes(k))) {
    return undefined;
  }
  return newChanges;
};

const cleanUpMapChanges = <T extends Changes>(
  mapChanges?: Record<ID, T>
): Record<ID, T> => {
  if (!mapChanges || !Object.keys(mapChanges).length) return {};

  return Object.fromEntries(
    Object.entries(mapChanges).filter(([, changes]) => {
      if (!changes) return false;

      const keys = Object.keys(changes);
      // If there are no changes left or only ids, return false
      return keys.length > 0 && keys.some(k => !idKeys.includes(k));
    })
  );
};

const cleanUpLineItemsChanges = (
  lineItemsChanges?: Record<ID, LineItemChanges>
): Record<ID, LineItemChanges> => {
  if (!lineItemsChanges || !Object.keys(lineItemsChanges).length) return {};

  return Object.fromEntries(
    Object.entries(lineItemsChanges).map(([id, changes]) => {
      const newLineItemChanges: LineItemChanges = { ...changes };

      // Remove empty custom column items
      newLineItemChanges.custom_column_items = cleanUpMapChanges(changes.custom_column_items);
      if (!Object.keys(newLineItemChanges.custom_column_items).length) {
        delete newLineItemChanges.custom_column_items;
      }
      const newLineItemKeys = Object.keys(newLineItemChanges);
      // If there are no changes left or only ids left, set to undefined
      if (!newLineItemKeys.length || newLineItemKeys.every(k => idKeys.includes(k))) {
        return [id, undefined];
      }
      return [id, newLineItemChanges];
    }).filter(([, changes]) => changes !== undefined)
  );
};

const cleanUpLotChanges = (lotChanges?: LotChanges): LotChanges => {
  if (!lotChanges) return {} as LotChanges;
  const newLotChanges: LotChanges = { ...lotChanges };

  // Remove empty custom column headers
  newLotChanges.custom_column_headers = cleanUpMapChanges(lotChanges.custom_column_headers);
  if (!Object.keys(newLotChanges.custom_column_headers).length) {
    delete newLotChanges.custom_column_headers;
  }

  // Remove empty line items
  newLotChanges.line_items = cleanUpLineItemsChanges(lotChanges.line_items);
  if (!Object.keys(newLotChanges.line_items).length) {
    delete newLotChanges.line_items;
  }

  const newLotKeys = Object.keys(newLotChanges);
  // If there are no changes left or only ids left, return an empty object
  if (!newLotKeys.length || newLotKeys.every(k => idKeys.includes(k))) {
    return {} as LotChanges;
  }
  return newLotChanges;
};

export const cleanUpChanges = (changes: Record<ID, LotChanges>): Record<ID, LotChanges> => {
  const newChanges: Record<ID, LotChanges> = {};

  Object.entries(changes).forEach(([lotId, lotChanges]) => {
    const newLotChanges: LotChanges = cleanUpLotChanges(lotChanges);

    if (newLotChanges && Object.keys(newLotChanges).length) {
      newChanges[lotId] = newLotChanges;
    }
  });

  return newChanges;
};
